/**
 * Creates the shared local-first read-model mechanics for one synchronized
 * entity type. Entity-specific adapters provide only materialization and patch
 * shape; persistence, pull handling, and ID-scoped reads stay centralized.
 *
 * @param {Object} options - Read-model configuration.
 * @param {string} options.entityType - Protocol entity type.
 * @param {Object} options.syncCoordinator - Shared sync coordinator.
 * @param {Function} [options.materialize] - Converts records to consumer data.
 * @param {Function} [options.buildChangeSet] - Builds an incremental patch.
 * @returns {Object} Generic synchronized entity read model.
 */
export const createSynchronizedEntityReadModel = ({
  entityType,
  syncCoordinator,
  materialize = (records) => records,
  buildChangeSet = ({ changes, records }) => ({ changes, records })
}) => {
  let localReadInFlight = null;

  const readLocalEntities = () => {
    if (!localReadInFlight) {
      localReadInFlight = syncCoordinator
        .readLocalEntities(entityType)
        .then(materialize)
        .finally(() => {
          localReadInFlight = null;
        });
    }

    return localReadInFlight;
  };

  const syncFromServer = () =>
    syncCoordinator.syncFromServer().then((result) => {
      if (result.status === 'error') throw result.error;

      if (result.status === 'resync_required') {
        const error = new Error(
          `Local ${entityType} data requires resynchronization`
        );
        error.code = result.status;
        throw error;
      }

      return readLocalEntities();
    });

  /**
   * Applies the entity-specific projection to records committed by sync.
   * Empty changes intentionally avoid opening another local read.
   *
   * @param {Object[]} changes - Applied metadata for this entity type.
   * @returns {Promise<Object>} Entity-specific incremental patch.
   */
  const readChangedEntities = (changes = []) => {
    if (changes.length === 0) {
      return Promise.resolve(buildChangeSet({ changes, records: [] }));
    }

    const ids = changes.map(({ entity_id }) => entity_id);
    return syncCoordinator
      .readLocalEntitiesByIds(entityType, ids)
      .then((records) => buildChangeSet({ changes, records }));
  };

  const subscribeChanges = (listener) =>
    syncCoordinator.subscribeEntityChanges(({ changes }) => {
      const entityChanges = changes[entityType] || [];
      if (entityChanges.length === 0) return undefined;
      return readChangedEntities(entityChanges).then(listener);
    });

  return {
    readLocalEntities,
    readChangedEntities,
    syncFromServer,
    syncChangesFromServer: () => syncCoordinator.syncFromServer(),
    close: syncCoordinator.close,
    getSyncState: syncCoordinator.getSyncState,
    subscribeSyncState: syncCoordinator.subscribeSyncState,
    subscribeChanges
  };
};
